import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function CTA() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    project: "general",
    message: "",
  });

  const projectOptions = [
    { id: "general", label: t("systemGeneral") },
    { id: "whatsapp-ai", label: t("systemWhatsappAi") },
    { id: "5vix-gym", label: t("system5vixGym") },
    { id: "5vix-fit", label: t("system5vixFit") },
    { id: "ai-medical", label: t("systemAiMedical") },
    { id: "textile-erp", label: t("systemTextileErp") },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const selectedOption = projectOptions.find((p) => p.id === formData.project);
    const projectName = selectedOption ? selectedOption.label : "General Services";

    // Format the message for WhatsApp
    const whatsappMessage = `*New Inquiry - Debug Platform*%0A%0A*Name:* ${encodeURIComponent(formData.name)}%0A*Company:* ${encodeURIComponent(formData.company || 'N/A')}%0A*Target Solution:* ${encodeURIComponent(projectName)}%0A%0A*Message:*%0A${encodeURIComponent(formData.message)}`;

    // Open WhatsApp with formatted message
    const whatsappUrl = `https://wa.me/201099289280?text=${whatsappMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    // Reset form
    setFormData({
      name: "",
      company: "",
      project: "general",
      message: "",
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section
      id="contact"
      className="py-24 bg-gradient-to-br from-[rgb(0,0,139)] to-[rgb(0,0,100)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side – Intro & Contact details */}
          <div className="text-white">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-white text-sm font-semibold">
                {t("getInTouch")}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t("ctaTitle")}
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              {t("ctaDescription")}
            </p>

            {/* Contact information */}
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">
                    {t("emailUs")}
                  </h3>
                  <p className="text-gray-300">contact@debuging.com</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">
                    {t("callUs")}
                  </h3>
                  <p className="text-gray-300">+20 109 9289 280</p>
                </div>
              </div>

              <div className="flex items-start">
                <a
                  href="https://wa.me/201099289280"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-start hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                    <i className="ri-whatsapp-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      WhatsApp
                    </h3>
                    <p className="text-gray-300">+20 109 9289 280</p>
                  </div>
                </a>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">
                    {t("availability")}
                  </h3>
                  <p className="text-gray-300">{t("availabilityText")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side – Contact form with Project Selection */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  {t("fullName")} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(0,0,139)] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  {t("companyName")}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(0,0,139)] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Your Company"
                />
              </div>

              {/* Target Project Dropdown */}
              <div>
                <label
                  htmlFor="project"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  {t("interestedSystem")}
                </label>
                <select
                  id="project"
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(0,0,139)] focus:border-transparent outline-none transition-all text-sm bg-white cursor-pointer"
                >
                  {projectOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  {t("projectDetails")} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(0,0,139)] focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your project or system requirements..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[rgb(0,0,139)] text-white py-3 rounded-lg font-semibold hover:bg-[rgb(0,0,100)] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-md"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                {t("sendMessage")}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Clicking send will connect you directly via WhatsApp
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}